// ===========================================
// Teacher Unavailability (Time Off) React Query Hooks
// ===========================================
// Teacher self-service: request / list / withdraw own time off.
// Admin: per-teacher periods, pending-approval inbox, set-away directly,
// approve / decline / cancel, affected-lessons preview.
//
// Approved periods change what's bookable everywhere (the conflict engine
// blocks away days), so mutations that create/approve/cancel an APPROVED
// period also invalidate the hybrid-booking slot caches.

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { teachersApi, TimeOffRangeInput } from '../api/users.api';
import { meetAndGreetKeys } from './useMeetAndGreet';
import { useInvalidateSlotCaches } from './useSlotCacheInvalidation';
import { userKeys } from './useUsers';

// ===========================================
// QUERY KEYS
// ===========================================

export const teacherUnavailabilityKeys = {
  all: ['teacherUnavailability'] as const,
  mine: () => [...teacherUnavailabilityKeys.all, 'mine'] as const,
  forTeacher: (teacherId: string) =>
    [...teacherUnavailabilityKeys.all, 'teacher', teacherId] as const,
  pending: () => [...teacherUnavailabilityKeys.all, 'pending'] as const,
};

// ===========================================
// SHARED INVALIDATION
// ===========================================

/**
 * An APPROVED away period (created, approved, or lifted) changes real
 * availability — refresh every surface that derives bookable slots (shared
 * slot caches via useInvalidateSlotCaches), PLUS meet & greet availability
 * (away periods filter the M&G teacher pool, unlike weekly windows) and the
 * teacher's own lists.
 *
 * ⚠️ The M&G half of that only became TRUE on 2026-09-02. `meetAndGreetKeys.all`
 * is ['meetAndGreets'], and the admin availability cache used to be a
 * root-level ['meetAndGreetAdminAvailability', …] literal — so this line
 * reached nothing availability-related and the `unavailablePeriods` block it
 * was written for was never refreshed. Nesting that key under `all` made the
 * claim real. Do not re-root it.
 */
function useInvalidateAvailabilitySurfaces() {
  const queryClient = useQueryClient();
  const invalidateSlotCaches = useInvalidateSlotCaches();
  return (teacherId?: string) => {
    queryClient.invalidateQueries({ queryKey: teacherUnavailabilityKeys.all });
    queryClient.invalidateQueries({ queryKey: meetAndGreetKeys.all });
    invalidateSlotCaches(teacherId ? [userKeys.teacherAvailability(teacherId)] : []);
  };
}

// ===========================================
// TEACHER SELF-SERVICE
// ===========================================

export function useMyUnavailability(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: teacherUnavailabilityKeys.mine(),
    queryFn: teachersApi.getMyUnavailability,
    enabled: options?.enabled ?? true,
  });
}

export function useRequestTimeOff() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: TimeOffRangeInput) => teachersApi.requestTimeOff(data),
    onSuccess: () => {
      // A new request is PENDING — it doesn't block availability yet, so only
      // the time-off lists need refreshing.
      queryClient.invalidateQueries({ queryKey: teacherUnavailabilityKeys.all });
    },
  });
}

export function useWithdrawTimeOff() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (periodId: string) => teachersApi.withdrawTimeOff(periodId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: teacherUnavailabilityKeys.all });
    },
  });
}

// ===========================================
// ADMIN
// ===========================================

export function useTeacherUnavailability(teacherId: string | null) {
  return useQuery({
    queryKey: teacherUnavailabilityKeys.forTeacher(teacherId || ''),
    queryFn: () => teachersApi.getUnavailability(teacherId!),
    enabled: !!teacherId,
  });
}

export function usePendingTimeOff(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: teacherUnavailabilityKeys.pending(),
    queryFn: teachersApi.getPendingTimeOff,
    enabled: options?.enabled ?? true,
  });
}

export function useSetTeacherAway() {
  const invalidate = useInvalidateAvailabilitySurfaces();
  return useMutation({
    mutationFn: ({ teacherId, data }: { teacherId: string; data: TimeOffRangeInput }) =>
      teachersApi.setTeacherAway(teacherId, data),
    onSuccess: (_, variables) => invalidate(variables.teacherId),
  });
}

export function useApproveTimeOff() {
  const invalidate = useInvalidateAvailabilitySurfaces();
  return useMutation({
    mutationFn: ({
      teacherId,
      periodId,
      reviewNote,
    }: {
      teacherId: string;
      periodId: string;
      reviewNote?: string;
    }) => teachersApi.approveTimeOff(teacherId, periodId, reviewNote),
    onSuccess: (_, variables) => invalidate(variables.teacherId),
  });
}

export function useDeclineTimeOff() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      teacherId,
      periodId,
      reviewNote,
    }: {
      teacherId: string;
      periodId: string;
      reviewNote?: string;
    }) => teachersApi.declineTimeOff(teacherId, periodId, reviewNote),
    onSuccess: () => {
      // Declined never blocked availability — lists only.
      queryClient.invalidateQueries({ queryKey: teacherUnavailabilityKeys.all });
    },
  });
}

export function useCancelTimeOff() {
  const invalidate = useInvalidateAvailabilitySurfaces();
  return useMutation({
    mutationFn: ({ teacherId, periodId }: { teacherId: string; periodId: string }) =>
      teachersApi.cancelTimeOff(teacherId, periodId),
    // Cancelling may LIFT an APPROVED block — availability surfaces refresh.
    onSuccess: (_, variables) => invalidate(variables.teacherId),
  });
}

export function useAwayAffectedLessons() {
  return useMutation({
    mutationFn: ({
      teacherId,
      startDate,
      endDate,
    }: {
      teacherId: string;
      startDate: string;
      endDate: string;
    }) => teachersApi.getAwayAffectedLessons(teacherId, startDate, endDate),
  });
}
